import {
  ProcessedSearchResult,
  ProcessedSearchResultItem,
} from '@app/data/clients/rbServerSideClient/queries/GetSearchResults/types';
import {
  SimilarDesignInventoryItem,
  SimilarDesignItem,
} from '@app/data/clients/rbServerSideClient/queries/GetSimilarDesigns/types';
import { GetArtistInventoryItemPortfolioQuery } from '@app/data/shop-bff';
import { getDiscountTypeAndAmount } from '@app/utils/getPriceComponent';
import logger from '@redbubble/next/logger';
import {
  Item as AnalyticsEcommerceItem,
  AnalyticsItemListID,
  AnalyticsItemListParams,
  AnalyticsItemsListIDNameMap,
  RbGtmClient,
} from '@redbubble/next-analytics/gtm';
import type { ArrayElement } from 'types';
import {
  DefaultInventoryItem,
  InventoryItem,
  ListsButtonInventoryItem,
  RecommendedProduct,
  Work,
} from '../../types/shop-bff/types';
import { getDiscountValue, getItemValue } from '../../utils/common';
import searchUUIDStorage, {
  SearchUUIDStorageKey,
  SearchUUIDStorageKeyId,
} from '../../utils/searchUUIDStorage';

export enum GtmEvent {
  ColorPickerPicked = 'color-picker-picked',
  ProductConfigChanged = 'product-config-changed',
  FollowArtist = 'Add',
  Click = 'Click',
  Scroll = 'Scroll',
  View = 'View',
  DiscoveryUnitTagClicked = 'discovery_unit_tag_clicked',
  ScrollToEnd = 'scroll-to-end',
}

export enum GACategory {
  WorkPage = 'Work Page',
  ProductPage = 'Product Page',
  Lists = 'Lists',
  Social = 'Social',
}

export enum AnalyticsComponentName {
  SimilarProducts = 'similar-products',
  SimilarDesigns = 'similar-designs',
  ArtistDetails = 'artist-details',
  AlsoAvailableOnModal = 'available_products',
  LicensedProduct = 'licensed-works',
  ViewMoreSimilarDesigns = 'view-more-similar-designs',
  EndofPageDiscoveryUnit = 'discovery_unit',
  SuggestedProducts = 'suggested-products',
  OffsiteSimilarDesigns = 'offsite-similar-designs',
  FanArt = 'fan-art',
  ProductFeed = 'product-feed',
  L2CategoryCarousel = 'l2-category-carousel',
}

/**
 * GA4 events
 */

export const trackGA4ViewItemEvent = ({
  item,
  itemListParams: { itemIndex, itemListID, itemListName },
}: {
  item: InventoryItem | SimilarDesignInventoryItem;
  itemListParams: AnalyticsItemListParams;
}) => {
  RbGtmClient.track({
    event: 'view_item',
    search_query_id:
      searchUUIDStorage.get(SearchUUIDStorageKey.LocationPathname) ?? '',
    ecommerce: {
      currency: item.price ? item.price.currency : '',
      items: [
        {
          discount: getDiscountValue(item.price),
          index: itemIndex,
          product_type_id: item.productTypeId,
          blank_item_id: item.blankItemId,
          work_id: String(item.workId),
          item_id: item.id,
          retargeting_id: item.marketingProductTypeId,
          inventory_item_id: item.id,
          item_list_id: itemListID,
          item_list_name: itemListName,
          price: item.price ? item.price.amount : null,
          quantity: 1,
          ...(item.gaCode && { item_category5: item.gaCode }),
        },
      ],
      value: item.price ? getItemValue(item.price) : '',
    },
  });
};

export const trackGA4SelectItemEvent = ({
  item,
  itemListParams: { itemIndex, itemListID, itemListName },
}: {
  item:
    | Omit<InventoryItem, '__typename'>
    | Omit<SimilarDesignInventoryItem, '__typename'>;
  itemListParams: AnalyticsItemListParams;
}) => {
  if (!itemListID || !itemListName) {
    const message =
      'Missing itemListID or itemListName while reporting select_item event.';
    logger.warn({ message, context: 'trackGA4SelectItemEvent' }, message);
    return;
  }

  const priceTypeAndAmount = getDiscountTypeAndAmount(
    item.volumeDiscount?.thresholds || [],
    item.price
  );

  if (!priceTypeAndAmount) return null;

  const { discountType, discountedAmount } = priceTypeAndAmount;
  const discountValue = getDiscountValue(item.price);

  RbGtmClient.track({
    event: 'select_item',
    search_query_id:
      searchUUIDStorage.get(SearchUUIDStorageKey.LocationPathname) ?? '',
    ecommerce: {
      item_list_id: itemListID,
      item_list_name: itemListName,
      items: [
        {
          discount: getDiscountValue(item.price),
          index: itemIndex,
          product_type_id: item.productTypeId,
          blank_item_id: item.blankItemId,
          work_id: String(item.workId),
          item_id: item.id,
          retargeting_id: item.marketingProductTypeId,
          inventory_item_id: item.id,
          item_list_id: itemListID,
          item_list_name: itemListName,
          price: item.price ? item.price.amount : null,
          quantity: 1,
          display_price: discountedAmount,
          discount_type: discountType,
          custom_discount: discountValue,
          ...(item.gaCode && { item_category5: item.gaCode }),
        },
      ],
    },
  });
};

/**
 * TODO:
 * Refactor parsing functions returning same shaped objects into a single
 * function accepting variations of arguments.
 * https://redbubble.atlassian.net/browse/DEP-3654
 */
export const parseDefaultInventoryItemAndWorkToAnalyticsEcommerceItem = ({
  inventoryItem,
  work,
  index,
}: {
  inventoryItem: NonNullable<DefaultInventoryItem>;
  work: Work;
  index?: number;
}): AnalyticsEcommerceItem => {
  return {
    discount: getDiscountValue(inventoryItem.price),
    index: index ?? null,
    product_type_id: inventoryItem.productTypeId,
    blank_item_id: inventoryItem.id.split('_')[2],
    work_id: String(work.id),
    item_id: inventoryItem.id,
    inventory_item_id: inventoryItem.id,
    price: inventoryItem.price ? inventoryItem.price.amount : null,
    quantity: 1,
  };
};

export const parseSimilarDesignItemToAnalyticsEcommerceItem = ({
  similarDesignItem,
  index,
}: {
  similarDesignItem: SimilarDesignItem;
  index?: number;
}): AnalyticsEcommerceItem | null => {
  if (!similarDesignItem.inventoryItem) return null;

  return {
    discount: getDiscountValue(similarDesignItem.inventoryItem.price),
    index: index ?? null,
    product_type_id: similarDesignItem.inventoryItem.productTypeId,
    blank_item_id: similarDesignItem.inventoryItem.id.split('_')[2],
    work_id: similarDesignItem.inventoryItem.work
      ? String(similarDesignItem.inventoryItem.work.id)
      : '',
    item_id: similarDesignItem.inventoryItem.id,
    inventory_item_id: similarDesignItem.inventoryItem.id,
    price: similarDesignItem.inventoryItem.price
      ? similarDesignItem.inventoryItem.price.amount
      : null,
    quantity: 1,
  };
};

type RecommendedInventoryItem = ArrayElement<
  NonNullable<
    GetArtistInventoryItemPortfolioQuery['shop_artistInventoryItemPortfolio']['results']
  >
>;
export const parseRecommendedInventoryItemToAnalyticsEcommerceItem = ({
  recommendedInventoryItem,
  index,
}: {
  recommendedInventoryItem: RecommendedInventoryItem;
  index: number;
}): AnalyticsEcommerceItem | null => {
  if (!recommendedInventoryItem || !recommendedInventoryItem.inventoryItem)
    return null;

  return {
    discount: getDiscountValue(recommendedInventoryItem.inventoryItem.price),
    index: index ?? null,
    product_type_id: recommendedInventoryItem.inventoryItem.productTypeId,
    blank_item_id: recommendedInventoryItem.inventoryItem.id.split('_')[2],
    work_id: recommendedInventoryItem.inventoryItem.work
      ? String(recommendedInventoryItem.inventoryItem.work.id)
      : '',
    item_id: recommendedInventoryItem.inventoryItem.id,
    inventory_item_id: recommendedInventoryItem.inventoryItem.id,
    price: recommendedInventoryItem.inventoryItem.price
      ? recommendedInventoryItem.inventoryItem.price.amount
      : null,
    quantity: 1,
  };
};

export const parseCarouselItemToAnalyticsEcommerceItem = ({
  item,
  index,
}: {
  item: RecommendedProduct | ProcessedSearchResult;
  index: number;
}): AnalyticsEcommerceItem | null => {
  if (!item || !item.inventoryItem) return null;

  return {
    discount: getDiscountValue(item.inventoryItem.price),
    index: index ?? null,
    product_type_id: item.inventoryItem.productTypeId,
    blank_item_id: item.inventoryItem.id.split('_')[2],
    work_id: item.inventoryItem.work.id,
    item_id: item.inventoryItem.id,
    inventory_item_id: item.inventoryItem.id,
    price: item.inventoryItem.price ? item.inventoryItem.price.amount : null,
    quantity: 1,
  };
};

export const trackGA4ViewItemListEvent = ({
  items,
  listID,
  topic,
  searchUUIDStorageKey,
}: {
  listID: string;
  items: AnalyticsEcommerceItem[];
  searchUUIDStorageKey: SearchUUIDStorageKeyId;
  topic?: string;
}) => {
  RbGtmClient.track({
    event: 'view_item_list',
    search_query_id: searchUUIDStorageKey
      ? (searchUUIDStorage.get(searchUUIDStorageKey) ?? null)
      : null,
    topic: topic ?? '',
    ecommerce: {
      item_list_id: listID,
      item_list_name: AnalyticsItemsListIDNameMap[listID] ?? '',
      items,
    },
  });
};

export const trackGA4AddToWishListEvent = ({
  item,
  workId,
  analyticsComponentID,
  itemIndex,
  searchUUIDStorageKey,
}: {
  item: ListsButtonInventoryItem | ProcessedSearchResultItem;
  workId: string;
  analyticsComponentID: AnalyticsItemListID;
  itemIndex: number;
  searchUUIDStorageKey?: SearchUUIDStorageKeyId;
}) => {
  RbGtmClient.track({
    event: 'add_to_wishlist',
    search_query_id: searchUUIDStorageKey
      ? (searchUUIDStorage.get(searchUUIDStorageKey) ?? '')
      : '',
    ecommerce: {
      currency: item.price ? item.price.currency : '',
      items: [
        {
          discount: getDiscountValue(item.price),
          index: itemIndex,
          product_type_id: item.productTypeId,
          blank_item_id: item.blankItemId,
          work_id: workId,
          item_id: item.id,
          retargeting_id: item.marketingProductTypeId,
          inventory_item_id: item.id,
          item_list_id: analyticsComponentID,
          item_list_name: AnalyticsItemsListIDNameMap[analyticsComponentID],
          price: item.price ? item.price.amount : null,
          quantity: 1,
          ...(item.gaCode && { item_category5: item.gaCode }),
        },
      ],
      value: item.price ? getItemValue(item.price) : '',
    },
  });
};
